import { ACTION_ICONS } from "../../../config/siteConfig";
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { Modal } from 'react-bootstrap';
import DetailView from '../../location/DetailView';
import { CSVLink } from 'react-csv';
import toast from 'react-hot-toast';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { useLoadingBar } from 'react-top-loading-bar';
import ContentLoader from '../../loader/ContentLoader';
import { SOMETHING_WENT_WRONG } from '../../../constants/strings';
import { rbacMenusService } from '../../../services/rbac-menus.service';
import { rbacModulesService } from '../../../services/rbac-modules.service';

const MySwal = withReactContent(Swal);

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

/* ---------- styles ---------- */
const labelStyle = { fontWeight: 600, fontSize: 12.5, color: "#334155", marginBottom: 5 };
const inputStyle = { borderRadius: 6, border: "1px solid #c3cad6", fontSize: 12, height: 34, background: "#fff" };
const filterInputStyle = { ...inputStyle, background: "#f8f9fe", border: "1px solid #e8edf5", borderRadius: 8 };
const STATUS_FILTER_OPTIONS = [{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }];
const selectStyles = {
  control: (base) => ({
    ...base, borderRadius: 6, border: "1px solid #c3cad6", minHeight: 34, height: 34,
    fontSize: 12, boxShadow: "none", "&:hover": { borderColor: "var(--btn-bg)" }
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 12 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};
const pageBtn = (active, disabled) => ({
  minWidth: 32, height: 32, padding: "0 8px", borderRadius: 8,
  border: active ? "1px solid var(--page-active-bg)" : "1px solid #e8edf5",
  background: active ? "var(--page-active-bg)" : "#fff",
  color: active ? "var(--page-active-text)" : disabled ? "#c3cad8" : "#444",
  fontSize: 13, fontWeight: 600, cursor: disabled ? "default" : "pointer"
});
const chip = (color, bg, border) => ({
  display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px",
  borderRadius: 10, background: bg, border: "1px solid " + border, color, fontSize: 13
});
const iconBtn = (bg, color) => ({
  width: 32, height: 32, borderRadius: 8, background: bg, color, border: 'none', padding: 0
});

const initialForm = { name: "", slug: "", route_to: "", module_id: "", parent_id: "", m_icon: "", ranking: "", status: "active" };
const initialFilters = { name: "", status: "" };

const RbacMenuList = () => {
  const { start, complete } = useLoadingBar();

  /* ---------- list state ---------- */
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [pageValue, setPageValue] = useState(1);
  const [pagingLimit, setPagingLimit] = useState("10");
  const [goPage, setGoPage] = useState("");
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  /* ---------- popup (add / edit / view) state ---------- */
  const [modal, setModal] = useState({ show: false, mode: "add", id: null });
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  /* ---------- dropdown sources (module / parent) ---------- */
  const [allMenus, setAllMenus] = useState([]);
  const [moduleOptions, setModuleOptions] = useState([]);
  const parentOptions = allMenus
    .filter(m => !m?.parent_id && m?.id !== modal.id)
    .map(m => ({ value: m.id, label: m.name }));

  const loadModules = async () => {
    const res = await rbacModulesService.list({ page: 1, status: "active" });    
    if (res?.success) {
      const rd = res.resData;
      setModuleOptions((Array.isArray(rd) ? rd : rd?.data || []).map(m => ({ value: m.id, label: m.name })));
    }
  };

  const loadAllMenus = async () => {
    const res = await rbacMenusService.list({ page: 1, status: "active" });
    if (res?.success) {
      const rd = res.resData;
      const rows = Array.isArray(rd) ? rd : rd?.data || [];
      setAllMenus(rows.filter(m => m?.parent_id === null || m?.parent_id === undefined));
    }
  };

  const totalRecords = pagination?.total || 0;
  const totalPage = pagination?.totalPages || 1;
  const activeCount = items.filter(i => i?.status === "active").length;
  const fromRecord = totalRecords === 0 ? 0 : (pageValue - 1) * Number(pagingLimit) + 1;
  const toRecord = Math.min(pageValue * Number(pagingLimit), totalRecords);
  const isView = modal.mode === "view";
  const isEdit = modal.mode === "edit";

  useEffect(() => {
    loadItems();
  }, [pagingLimit, pageValue, appliedFilters]);

  useEffect(() => {
    loadAllMenus();
    loadModules();
  }, []);

  /* ---------- API: list ---------- */
  const loadItems = async () => {
    try {
      setLoading(true);
      start();
      const query = { page: Number(pageValue), limit: Number(pagingLimit) };
      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (value !== "" && value !== null) query[key] = value;
      });
      const res = await rbacMenusService.list(query);
      if (res?.success) {
        const rd = res.resData;
        let rows = Array.isArray(rd) ? rd : rd?.data || [];
        let pg = (Array.isArray(rd) ? res.pagination : rd?.pagination) || null;
        if (!pg) {
          // API returned the full list without pagination info: paginate on the client
          const limit = Number(pagingLimit);
          pg = { total: rows.length, totalPages: Math.max(1, Math.ceil(rows.length / limit)) };
          rows = rows.slice((Number(pageValue) - 1) * limit, Number(pageValue) * limit);
        }
        setItems(rows);
        setPagination(pg);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      MySwal.fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
    } finally {
      setLoading(false);
      complete();
    }
  };

  /* ---------- filters ---------- */
  const handleFilterText = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });

  const handleApply = (e) => {
    e?.preventDefault();
    setPageValue(1);
    setAppliedFilters({ ...filters });
  };

  const handleReset = () => {
    setFilters(initialFilters);
    setPageValue(1);
    setAppliedFilters(initialFilters);
  };

  /* ---------- pagination ---------- */
  const handlePagingLimit = (e) => { setPageValue(1); setPagingLimit(e.target.value); };
  const handlePagination = (n) => setPageValue(n);
  const handleGoPage = () => {
    const n = Number(goPage);
    if (n >= 1 && n <= totalPage) setPageValue(n);
    setGoPage("");
  };
  const getPageNumbers = () => {
    if (totalPage <= 5) return Array.from({ length: totalPage }, (_, i) => i + 1);
    const sorted = [...new Set([1, 2, totalPage - 1, totalPage, pageValue - 1, pageValue, pageValue + 1])]
      .filter(n => n >= 1 && n <= totalPage).sort((a, b) => a - b);
    const out = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) out.push("...");
      out.push(n);
    });
    return out;
  };

  /* ---------- row <-> form <-> payload ---------- */
  const rowToForm = (row) => ({ name: row?.name ?? "", slug: row?.slug ?? "", route_to: row?.route_to ?? "", module_id: row?.module_id ?? "", parent_id: row?.parent_id ?? "", m_icon: row?.m_icon ?? "", ranking: row?.ranking ?? "", status: row?.status || "active" });

  const formToPayload = (f) => ({ name: String(f.name ?? "").trim(), slug: String(f.slug ?? "").trim(), route_to: String(f.route_to ?? "").trim(), module_id: Number(f.module_id) || 0, parent_id: f.parent_id ? Number(f.parent_id) : null, m_icon: String(f.m_icon ?? "").trim(), ranking: f.ranking === "" ? 0 : Number(f.ranking), status: f.status });

  /* ---------- popup open / close ---------- */
  const closeModal = () => setModal({ ...modal, show: false });

  const openAdd = () => {
    setForm(initialForm);
    setModal({ show: true, mode: "add", id: null });
  };

  const openExisting = async (row, mode) => {
    setModal({ show: true, mode, id: row?.id });
    setForm(initialForm);
    try {
      setModalLoading(true);
      const res = await rbacMenusService.details(row?.id);
      if (!res?.success) {
        toast.error(res?.message);
        setModal(m => ({ ...m, show: false }));
        return;
      }
      setForm(rowToForm(res.resData?.data ?? res.resData));
    } catch (err) {
      MySwal.fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
      setModal(m => ({ ...m, show: false }));
    } finally {
      setModalLoading(false);
    }
  };

  const handleFormText = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  /* ---------- API: create / update (with confirmation popup) ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!String(form.name ?? "").trim() || form.name === 0) { toast.error("Menu Name is required"); return; }
    if (!String(form.slug ?? "").trim() || form.slug === 0) { toast.error("Slug is required"); return; }
    if (!String(form.module_id ?? "").trim() || form.module_id === 0) { toast.error("Module is required"); return; }

    const confirm = await MySwal.fire({
      title: isEdit ? "Update Menu?" : "Save Menu?",
      text: isEdit ? "Do you want to update this menu?" : "Do you want to add this menu?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isEdit ? "Yes, update" : "Yes, save",
      cancelButtonText: "Cancel"
    });
    if (!confirm.isConfirmed) return;

    try {
      setSaving(true);
      const payload = formToPayload(form);
      const res = isEdit
        ? await rbacMenusService.update(modal.id, payload)
        : await rbacMenusService.create(payload);
      if (res?.success) {
        toast.success(res.message);
        closeModal();
        loadItems(); loadAllMenus();
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      MySwal.fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
    } finally {
      setSaving(false);
    }
  };

  /* ---------- API: status change (with confirmation) ---------- */
  const handleStatusChange = async (row, newStatus) => {
    if (newStatus === row?.status) return;
    const result = await MySwal.fire({
      title: "Change status?",
      text: "Are you sure you want to mark this menu as " + newStatus + "?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, change it",
      cancelButtonText: "Cancel"
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await rbacMenusService.update(row?.id, { ...formToPayload(rowToForm(row)), status: newStatus });
      if (res?.success) toast.success(res.message); else toast.error(res?.message);
    } catch (err) {
      MySwal.fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
    } finally {
      setLoading(false);
      loadItems();
    }
  };

  /* ---------- API: delete (with confirmation) ---------- */
  const handleDelete = async (row) => {
    const result = await MySwal.fire({
      title: "Delete Menu?",
      text: "This cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d93025",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel"
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await rbacMenusService.delete(row?.id);
      if (res?.success) toast.success(res.message); else toast.error(res?.message);
    } catch (err) {
      MySwal.fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
    } finally {
      setLoading(false);
      loadItems(); loadAllMenus();
    }
  };

  /* ---------- export ---------- */
  const csvHeaders = [
    { label: "Menu", key: "c0" },
    { label: "Slug", key: "c1" },
    { label: "Route", key: "c2" },
    { label: "Module", key: "c3" },
    { label: "Parent", key: "c4" },
    { label: "Ranking", key: "c5" },
    { label: "Status", key: "status" },
  ];
  const csvData = items.map(row => ({
    c0: row?.name,
    c1: row?.slug,
    c2: row?.route_to,
    c3: row?.module?.name,
    c4: row?.parent?.name,
    c5: row?.ranking,
    status: row?.status,
  }));

  return (
    <>
      <section className="section dashboard">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4 className="m-0 fw-bold" style={{ color: "var(--page-heading)", fontSize: 20 }}>RBAC Menus</h4>
            <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#475569" }}>
              <Link to="/dashboard" style={{ color: "#475569" }}><i className="bi bi-house-door"></i></Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>RBAC Menu</span>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button type="button" onClick={openAdd} className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
              style={{ background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
              <i className="bi bi-plus-lg"></i> Add Menu
            </button>
            <CSVLink data={csvData} headers={csvHeaders} filename="rbac-menus.csv"
              className="btn btn-sm d-flex align-items-center gap-1 px-3"
              style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}>
              <i className="bi bi-download"></i> Export
            </CSVLink>
          </div>
        </div>

        {/* Filters */}
        <form onSubmit={handleApply} className="card border-0 shadow-sm mb-3" style={{ borderRadius: 14 }}>
          <div className="card-body p-3">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md">
                <input type="text" className="form-control" name="name" placeholder="Search menu name"
                  value={filters.name} onChange={handleFilterText} style={filterInputStyle} />
              </div>
              <div className="col-6 col-md-2">
                <Select
                  options={STATUS_FILTER_OPTIONS}
                  value={STATUS_FILTER_OPTIONS.find(o => o.value === filters.status) || null}
                  onChange={(o) => setFilters({ ...filters, status: o ? o.value : "" })}
                  placeholder="Select Status"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-12 col-md-auto d-flex gap-2">
                <button type="submit" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
                  style={{ background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
                  <i className="bi bi-search"></i> Apply
                </button>
                <button type="button" onClick={handleReset} className="btn btn-sm d-flex align-items-center gap-1 px-3"
                  style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}>
                  <i className="bi bi-arrow-counterclockwise"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* Stat chips */}
        <div className="d-flex flex-wrap gap-2 mb-3">
          <span style={chip("#e8590c", "#fff4e6", "#ffd8a8")}><i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i><b>{totalRecords}</b> Total RBAC Menus</span>
          <span style={chip("#0f8a5f", "#e6f7f1", "#b7ebd6")}><i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i><b>{activeCount}</b> Active on this page</span>
          <span style={chip("#1971c2", "#e7f5ff", "#a5d8ff")}><i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i><b>{items.length - activeCount}</b> Inactive on this page</span>
        </div>

        {/* Table */}
        <div className="card border-0 shadow-sm" style={{ borderRadius: 14, overflow: "hidden" }}>
          <div style={{ position: 'relative' }}>
            {loading && (
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(255,255,255,0.6)', display: 'flex',
                justifyContent: 'center', alignItems: 'center', zIndex: 10
              }}>
                <ContentLoader loaderType="circleSpin" />
              </div>
            )}
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr style={{ background: "#f8f9fe" }}>
                    {["S.No.", "Menu","Slug","Route","Module","Parent","Ranking","Status","Actions"].map((h, i) => (
                      <th key={i} style={{
                        fontSize: "11px", fontWeight: "700", color: "#475569",
                        textTransform: "uppercase", letterSpacing: "0.6px",
                        borderBottom: "2px solid #e8edf5", padding: "10px 12px",
                        whiteSpace: "nowrap", background: "#f8f9fe"
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((row, sno_i) => (
                    <tr key={row?.id}>
<td className="ps-3" style={{ fontSize: 13 }}>{(pageValue - 1) * Number(pagingLimit) + sno_i + 1}</td>
                      <td style={{ fontSize: 13 }}>{row?.name ?? "-"}</td>
                      <td style={{ fontSize: 13 }}>{row?.slug ?? "-"}</td>
                      <td style={{ fontSize: 13 }}>{row?.route_to ?? "-"}</td>
                      <td style={{ fontSize: 13 }}>{row?.module?.name ?? "-"}</td>
                      <td style={{ fontSize: 13 }}>{row?.parent?.name ?? "-"}</td>
                      <td style={{ fontSize: 13 }}>{row?.ranking ?? "-"}</td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={row?.status}
                          onChange={(e) => handleStatusChange(row, e.target.value)}
                          style={{
                            width: 'auto', minWidth: 0, height: 28, padding: '0 26px 0 10px', backgroundPosition: 'right 8px center',
                            borderRadius: 8, fontSize: 12, fontWeight: 600,
                            backgroundColor: row?.status === "active" ? '#d4edda' : '#f8d7da',
                            color: row?.status === "active" ? '#155724' : '#721c24',
                            border: 'none'
                          }}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          {row?.status !== "active" && row?.status !== "inactive" && <option value={row?.status}>{row?.status}</option>}
                        </select>
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <button className="btn btn-sm d-flex align-items-center justify-content-center" title="View"
                            onClick={() => openExisting(row, "view")} style={iconBtn('#e6f7f1', '#0f8a5f')}>
                            <i className={`bi ${ACTION_ICONS.view}`} data-action="view" style={{ fontSize: 13 }}></i>
                          </button>
                          <button className="btn btn-sm d-flex align-items-center justify-content-center" title="Edit"
                            onClick={() => openExisting(row, "edit")} style={iconBtn('#e8f0fe', 'var(--btn-bg)')}>
                            <i className={`bi ${ACTION_ICONS.edit}`} data-action="edit" style={{ fontSize: 13 }}></i>
                          </button>
                          <button className="btn btn-sm d-flex align-items-center justify-content-center" title="Delete"
                            onClick={() => handleDelete(row)} style={iconBtn('#fce8e6', '#d93025')}>
                            <i className={`bi ${ACTION_ICONS.delete}`} data-action="delete" style={{ fontSize: 13 }}></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && items.length === 0 && (
                    <tr><td colSpan={9} className="text-center text-muted py-4">No records found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-3" style={{ borderTop: "1px solid #eef1f6", fontSize: 13, color: "#6b7a90" }}>
            <div>Showing <b>{fromRecord}–{toRecord}</b> of <b>{totalRecords}</b> menus</div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span>Rows</span>
              <select className="form-select form-select-sm" style={{ width: 100, borderRadius: 8 }} value={pagingLimit} onChange={handlePagingLimit}>
                {["10", "20", "50", "100", "500"].map(v => <option key={v} value={v}>{v}/page</option>)}
              </select>
              <span>Go</span>
              <input type="number" min="1" max={totalPage} className="form-control form-control-sm text-center" style={{ width: 60, borderRadius: 8 }}
                value={goPage} onChange={(e) => setGoPage(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleGoPage()} placeholder={String(pageValue)} />
              <span>/ {totalPage}</span>
              <button type="button" style={pageBtn(false, false)} onClick={handleGoPage}>Go</button>
              <div className="d-flex align-items-center gap-1">
                <button type="button" disabled={pageValue <= 1} style={pageBtn(false, pageValue <= 1)} onClick={() => handlePagination(1)}><i className="bi bi-chevron-double-left"></i></button>
                <button type="button" disabled={pageValue <= 1} style={pageBtn(false, pageValue <= 1)} onClick={() => handlePagination(pageValue - 1)}><i className="bi bi-chevron-left"></i></button>
                {getPageNumbers().map((n, i) => n === "..."
                  ? <span key={"e" + i} className="px-1">...</span>
                  : <button type="button" key={n} style={pageBtn(n === pageValue, false)} onClick={() => handlePagination(n)}>{n}</button>)}
                <button type="button" disabled={pageValue >= totalPage} style={pageBtn(false, pageValue >= totalPage)} onClick={() => handlePagination(pageValue + 1)}><i className="bi bi-chevron-right"></i></button>
                <button type="button" disabled={pageValue >= totalPage} style={pageBtn(false, pageValue >= totalPage)} onClick={() => handlePagination(totalPage)}><i className="bi bi-chevron-double-right"></i></button>
              </div>
            </div>
          </div>
        </div>

        {/* Add / Update / View popup */}
        <Modal show={modal.show} onHide={closeModal} centered size={isView ? "lg" : undefined} scrollable>
          <Modal.Header closeButton closeVariant={isView ? "white" : undefined} style={isView ? { background: "linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)", borderBottom: "none" } : undefined}>
            <div>
              <Modal.Title className="d-flex align-items-center gap-2" style={isView ? { color: "#fff", fontSize: 18, fontWeight: 600 } : { fontSize: 16, fontWeight: 700 }}>
                {isView && <i className={`bi ${ACTION_ICONS.view}`} data-action="view"></i>}
                {isView ? "Menu Details" : isEdit ? "Update Menu" : "Add Menu"}
              </Modal.Title>
              {!isView && <div style={{ fontSize: 12, color: "rgba(255,255,255,.78)", marginTop: 2 }}>
                {isView ? "View menu information" : isEdit ? "Update menu information" : "Add a new menu to the system"}
              </div>}
            </div>
          </Modal.Header>
          <form onSubmit={handleSubmit}>
            <Modal.Body style={isView ? { padding: 24, background: "#f8f9fe" } : undefined}>
              {modalLoading ? <ContentLoader loaderType="circleSpin" /> : isView ? (
                <DetailView fields={[
                  { label: "Menu Name", value: form.name },
                  { label: "Slug", value: form.slug },
                  { label: "Route", value: form.route_to },
                  { label: "Module", value: moduleOptions.find(o => o.value === Number(form.module_id))?.label },
                  { label: "Parent Menu", value: form.parent_id ? parentOptions.find(o => o.value === Number(form.parent_id))?.label : "None" },
                  { label: "Icon", value: form.m_icon },
                  { label: "Ranking", value: form.ranking },
                  { label: "Status", value: form.status, badge: true },
                ]} />
              ) : (
                <div className="row g-3">
                  <div className="col-12">
                    <div style={labelStyle}>Menu Name{!isView && <span className="text-danger"> *</span>}</div>
                    <input type="text" className="form-control" name="name" placeholder="e.g. Flights"
                      value={form.name} onChange={handleFormText} style={inputStyle} />
                  </div>
                  <div className="col-6">
                    <div style={labelStyle}>Slug{!isView && <span className="text-danger"> *</span>}</div>
                    <input type="text" className="form-control" name="slug" placeholder="e.g. flights"
                      value={form.slug} onChange={handleFormText} style={inputStyle} />
                  </div>
                  <div className="col-6">
                    <div style={labelStyle}>Route</div>
                    <input type="text" className="form-control" name="route_to" placeholder="e.g. /flight/list"
                      value={form.route_to} onChange={handleFormText} style={inputStyle} />
                  </div>
                  <div className="col-6">
                    <div style={labelStyle}>Module{!isView && <span className="text-danger"> *</span>}</div>
                    <Select options={moduleOptions} value={moduleOptions.find(o => o.value === Number(form.module_id)) || null}
                      onChange={(o) => setForm({ ...form, module_id: o ? o.value : 0 })} placeholder="Select module"
                      menuPortalTarget={document.body} styles={selectStyles} />
                  </div>
                  <div className="col-6">
                    <div style={labelStyle}>Parent Menu</div>
                    <Select options={parentOptions} value={parentOptions.find(o => o.value === Number(form.parent_id)) || null}
                      onChange={(o) => setForm({ ...form, parent_id: o ? o.value : "" })} isClearable placeholder="None (top level)"
                      menuPortalTarget={document.body} styles={selectStyles} />
                  </div>
                  <div className="col-6">
                    <div style={labelStyle}>Icon</div>
                    <input type="text" className="form-control" name="m_icon" placeholder="e.g. bi bi-airplane"
                      value={form.m_icon} onChange={handleFormText} style={inputStyle} />
                  </div>
                  <div className="col-6">
                    <div style={labelStyle}>Ranking</div>
                    <input type="number" className="form-control" name="ranking" placeholder="e.g. 1" min="0" step="any"
                      value={form.ranking} onChange={handleFormText} style={inputStyle} />
                  </div>
                  <div className="col-6">
                    <div style={labelStyle}>Status</div>
                    <Select
                      options={STATUS_OPTIONS}
                      value={STATUS_OPTIONS.find(o => o.value === form.status) || null}
                      onChange={(o) => setForm({ ...form, status: o ? o.value : "active" })}
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                    />
                  </div>
                </div>
              )}
            </Modal.Body>
            {!isView && <Modal.Footer className="justify-content-end gap-2">
              <button type="button" onClick={closeModal} className="btn btn-sm px-4"
                style={{ background: "#fff", border: "1px solid #c3cad6", borderRadius: 6, height: 34, fontSize: 13, color: "#444" }}>
                {isView ? "Close" : "Cancel"}
              </button>
              {!isView && (
                <button type="submit" disabled={saving || modalLoading} className="btn btn-sm px-4 text-white fw-semibold"
                  style={{ background: "var(--btn-bg)", border: "none", borderRadius: 6, height: 34, fontSize: 13 }}>
                  {isEdit ? "Update Menu" : "Add Menu"}
                </button>
              )}
            </Modal.Footer>}
          </form>
        </Modal>
      </section>
    </>
  )
}

export default RbacMenuList;
